import { FORM_COLOR_DEFAULT_KEYS, colorValue } from "@jobshop/crossroads/components/Forms/formColorDefaults.js"
import type { ColorSlot } from "@/utils/colorSlots"
import { resolveColorSlots } from "@/utils/colorSlots"

/**
 * Shows a form in the jobshop theme (JET-2975).
 *
 * A form built in the recruiter editor stores palette references (`{ key, label, value }`),
 * so its colours follow the jobshop palette like any content module. A colour the editor
 * left empty takes the palette slot crossroads names as its default in FORM_COLOR_DEFAULT_KEYS.
 * Only colours of parts the form already has are filled: an `applyButton` or `successPage`
 * that is absent stays absent, since creating one changes what the renderer shows.
 *
 * Forms from the Backoffice store plain hex strings. They hold no reference, so they are
 * returned unchanged: no colour is filled in, and the renderer keeps its own fallbacks.
 */
export function resolveFormColors(config: FormConfig, palette: Record<string, ColorSlot>): FormConfig {
  const resolved = resolveColorSlots(config, palette)
  const form = resolved?.form
  if (!form || !usesPaletteRefs(form))
    return resolved

  let next: Record<string, any> = form
  for (const [path, slotKey] of Object.entries(FORM_COLOR_DEFAULT_KEYS)) {
    const slot = palette[slotKey]
    if (slot && hasParent(next, path) && !colorValue(valueAt(next, path)))
      next = withValueAt(next, path, { key: slot.key, label: slot.label, value: slot.value })
  }

  return next === form ? resolved : { ...resolved, form: next as Form }
}

function usesPaletteRefs(form: Form): boolean {
  return Object.keys(FORM_COLOR_DEFAULT_KEYS).some((path) => {
    const node = valueAt(form, path)
    return !!node && typeof node === "object" && typeof node.key === "string"
  })
}

function hasParent(node: any, path: string): boolean {
  const parentPath = path.split(".").slice(0, -1).join(".")
  if (!parentPath)
    return true
  const parent = valueAt(node, parentPath)
  return !!parent && typeof parent === "object"
}

function valueAt(node: any, path: string): any {
  return path.split(".").reduce((current, segment) => (current && typeof current === "object" ? current[segment] : undefined), node)
}

/** Copies only the objects along `path`, so the payload passed in stays untouched. */
function withValueAt(node: Record<string, any>, path: string, value: unknown): Record<string, any> {
  const [head, ...rest] = path.split(".")
  if (rest.length === 0)
    return { ...node, [head]: value }

  return { ...node, [head]: withValueAt(node[head], rest.join("."), value) }
}
